import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import defaultStyles from './FilterMenu.module.css';
import jlrStyles from './FilterMenuJlr.module.css';
import {
  Button,
  Popover,
  Paper,
  MenuList,
  Checkbox,
  ListItemText,
  Typography,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Icon,
} from '@material-ui/core';
import { APPLICATION_VARIANTS } from '../../constants';
import config from '../../config/config';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Erase from '../../icons/eraser.svg';
import GreenErase from '../../icons/green-eraser.svg';
import { JLR_PLANT } from '../../constants';
import { setStylesAccordingToPlant } from '../../utils/utils';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';

const FilterMenu = ({
  open,
  anchorEl,
  handleClose,
  handleFilter,
  filterOptions,
  rows,
}) => {
  const [filters, setFilters] = useState({});

  const [plant] = usePlantAndLinesStore().plantState;
  const [styles, setStyles] = useState(defaultStyles);

  const toggleFilterParameter = (filterName, filterParameter) => {
    const selectedFilterParameters =
      filters[filterName].selectedFilterParameters;
    if (selectedFilterParameters.has(filterParameter)) {
      selectedFilterParameters.delete(filterParameter);
      return setFilters(prevState => ({
        ...prevState,
        [filterName]: {
          ...prevState[filterName],
          selectedFilterParameters: selectedFilterParameters,
        },
      }));
    }

    selectedFilterParameters.add(filterParameter);
    return setFilters(prevState => ({
      ...prevState,
      [filterName]: {
        ...prevState[filterName],
        selectedFilterParameters: selectedFilterParameters,
      },
    }));
  };

  const clearSelectedFilters = () => {
    Object.keys(filters).forEach(filterField => {
      setFilters(prevState => ({
        ...prevState,
        [filterField]: {
          ...prevState[filterField],
          selectedFilterParameters: new Set(),
        },
      }));
    });
  };

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  useEffect(() => {
    const buildFilters = rows => {
      const allDataAtRootLevel = rows.map(planData => {
        const { plan, attributes, ...rest } = planData;
        return { ...rest, ...plan, ...attributes };
      });

      filterOptions.forEach(({ field, name }) => {
        const parameters = allDataAtRootLevel
          .map(row => row[field])
          .filter(option => Boolean(option));
        setFilters(prevState => ({
          ...prevState,
          [field]: {
            filterParameters: new Set(parameters),
            selectedFilterParameters: new Set(),
            name,
          },
        }));
      });
    };
    buildFilters(rows);
  }, [filterOptions, rows]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Paper className={styles.menu}>
        <MenuList autoFocusItem={open}>
          <span className={styles.menuTitle}>
            Filter
            <Button
              className={styles.clearButton}
              variant="contained"
              onClick={() => clearSelectedFilters()}
            >
              {' '}
              <Icon className={styles.eraseIcon}>
                <img
                  src={plant.label === JLR_PLANT ? GreenErase : Erase}
                  alt="Clear All"
                  className={styles.eraseIcon}
                />
              </Icon>
              Clear all
            </Button>
          </span>
          <div
            className={
              config?.APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4
                ? styles.menuInner
                : styles.menuInnerForPV
            }
          >
            {Object.keys(filters).map(filterField => {
              const filter = filters[filterField];
              return (
                <ExpansionPanel className={styles.filterItem} key={filterField}>
                  <ExpansionPanelSummary
                    className={styles.filterInner}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography className={styles.heading}>
                      {filter.name}
                    </Typography>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails className={styles.subFileter}>
                    {[...filter.filterParameters].map(filterParameter => (
                      <React.Fragment key={filterParameter}>
                        <div className={styles.subFilterItem}>
                          <Checkbox
                            className={styles.checkBox}
                            color="default"
                            checked={filter.selectedFilterParameters.has(
                              filterParameter,
                            )}
                            onChange={() =>
                              toggleFilterParameter(
                                filterField,
                                filterParameter,
                              )
                            }
                          />
                          <ListItemText>
                            <span className={styles.listItem}>
                              {filterParameter}
                            </span>
                          </ListItemText>
                        </div>
                      </React.Fragment>
                    ))}
                  </ExpansionPanelDetails>
                </ExpansionPanel>
              );
            })}
          </div>

          <Button
            className={styles.menuButton}
            variant="contained"
            onClick={() => handleFilter(filters)}
          >
            Apply
          </Button>
        </MenuList>
      </Paper>
    </Popover>
  );
};

FilterMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.instanceOf(Element),
  handleClose: PropTypes.func.isRequired,
  filters: PropTypes.object,
  handleFilter: PropTypes.func.isRequired,
  rows: PropTypes.array.isRequired,
  filterOptions: PropTypes.array.isRequired,
};

export default FilterMenu;
